import axios from 'axios';
import { config } from '../config';

const API_URL = config.apiUrl;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// User API
export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  updateProfile: (name: string, email: string) => 
    api.put('/user/profile', { name, email }),
  changePassword: (currentPassword: string, newPassword: string) => 
    api.post('/user/change-password', { current_password: currentPassword, new_password: newPassword }),
  getKeys: () => api.get('/user/keys'),
  redeemKey: (apiKey: string) => api.post('/user/redeem-code', { api_key: apiKey }),
  getIPs: (apiKey: string) => 
    api.post('/user/ips/list', { api_key: apiKey }),
  addIP: (apiKey: string, ipAddress: string) => 
    api.post('/user/ips/add', { api_key: apiKey, ip_address: ipAddress }),
  addCurrentIP: (apiKey: string) => 
    api.post('/user/ips/add-current', { api_key: apiKey }),
  removeIP: (apiKey: string, ipAddress: string) => 
    api.post('/user/ips/remove', { api_key: apiKey, ip_address: ipAddress }),
};

// Account API (using API key)
export const accountAPI = {
  getInfo: (apiKey: string) => 
    api.get('/v1/account/info', {
      headers: { Authorization: `Bearer ${apiKey}` },
    }),
  getIPs: (apiKey: string) => 
    api.get('/v1/account/ips', {
      headers: { Authorization: `Bearer ${apiKey}` },
    }),
  removeIP: (apiKey: string, ipAddress: string) => 
    api.post('/v1/account/ips/remove', 
      { ip_address: ipAddress },
      { headers: { Authorization: `Bearer ${apiKey}` } }
    ),
  getUsage: (apiKey: string) => 
    api.get('/v1/account/usage', {
      headers: { Authorization: `Bearer ${apiKey}` },
    }),
};

// Models API
export const modelsAPI = {
  list: () => api.get('/v1/models'),
};

// Admin API
const getAdminToken = () => {
  return localStorage.getItem('admin_token') || '';
};

const getAdminHeaders = () => {
  const token = getAdminToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const adminAPI = {
  // Stats
  getStats: () => 
    api.get('/admin/stats', {
      headers: getAdminHeaders(),
    }),
  
  // API Keys
  listAPIKeys: () => 
    api.get('/admin/keys/list', {
      headers: getAdminHeaders(),
    }),
  
  getAPIKeyInfo: (apiKey: string) => 
    api.get('/admin/keys/info', {
      headers: getAdminHeaders(),
      params: { api_key: apiKey },
    }),
  
  createAPIKey: (data: { name: string; email: string; plan: string; expiry_days?: number; notes?: string }) => 
    api.post('/admin/keys/create', data, {
      headers: getAdminHeaders(),
    }),
  
  deleteAPIKey: (apiKey: string) => 
    api.delete('/admin/keys/delete', {
      headers: getAdminHeaders(),
      params: { api_key: apiKey },
    }),
  
  updateAPIKeyStatus: (apiKey: string, status: string) => 
    api.post('/admin/keys/status', 
      { api_key: apiKey, status },
      { headers: getAdminHeaders() }
    ),
  
  // IP Management
  addIPToKey: (apiKey: string, ipAddress: string) => 
    api.post('/admin/keys/ips/add', 
      { api_key: apiKey, ip_address: ipAddress },
      { headers: getAdminHeaders() }
    ),
  
  removeIPFromKey: (apiKey: string, ipAddress: string) => 
    api.post('/admin/keys/ips/remove', 
      { api_key: apiKey, ip_address: ipAddress },
      { headers: getAdminHeaders() }
    ),
  
  resetAllIPs: (apiKey: string) => 
    api.post('/admin/keys/ips/reset', 
      { api_key: apiKey },
      { headers: getAdminHeaders() }
    ),
  
  // Rate Limits
  resetRateLimit: (apiKey: string) => 
    api.post('/admin/keys/ratelimit/reset', 
      { api_key: apiKey },
      { headers: getAdminHeaders() }
    ),
  
  // Expiry
  extendExpiry: (apiKey: string, days: number) => 
    api.post('/admin/keys/expiry/extend', 
      { api_key: apiKey, days },
      { headers: getAdminHeaders() }
    ),
  
  setExpiry: (apiKey: string, expiresAt: string) => 
    api.post('/admin/keys/expiry/set', 
      { api_key: apiKey, expires_at: expiresAt },
      { headers: getAdminHeaders() }
    ),
  
  // Redeem Codes
  listRedeemCodes: () => 
    api.get('/admin/redeem/list', {
      headers: getAdminHeaders(),
    }),
  
  getRedeemCode: (code: string) => 
    api.get(`/admin/redeem/${code}`, {
      headers: getAdminHeaders(),
    }),
  
  createRedeemCodes: (data: { name: string; plan: string; quantity: number; duration_days: number; notes?: string }) => 
    api.post('/admin/redeem/create', data, {
      headers: getAdminHeaders(),
    }),
  
  deleteRedeemCode: (code: string) => 
    api.delete(`/admin/redeem/${code}`, {
      headers: getAdminHeaders(),
    }),
  
  getRedeemStats: () => 
    api.get('/admin/redeem-stats', {
      headers: getAdminHeaders(),
    }),
};

export default api;